import { useState } from 'react'
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import { signOut } from '../firebase/auth'
import './Dashboard.css'

const Dashboard = ({ user, claims }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const [showMobileMenu, setShowMobileMenu] = useState(false)
  const { isAdmin, isEditor, isSdr } = claims ?? { isAdmin: false, isEditor: false, isSdr: false }

  const navItems = [
    { path: '/contacts', label: 'Contacts', icon: '👥' },
    { path: '/flows', label: 'Flows', icon: '🔄' },
    { path: '/deals', label: 'Deals', icon: '💼' },
    { path: '/analytics', label: 'Analytics', icon: '📊' },
    { path: '/tags', label: 'Tags', icon: '🏷️' },
    { path: '/files', label: 'Files', icon: '📁' },
    { path: '/notes', label: 'My Notes', icon: '📓' },
    { path: '/bant-ref', label: 'BANT Ref', icon: '✅' },
    { path: '/icp-hub', label: 'ICP Hub', icon: '🎯' },
    { path: '/pricing-playbook', label: 'Pricing Playbook', icon: '💰' },
    { path: '/price-list', label: 'Price List', icon: '📋' },
    { path: '/aisec-markets', label: 'AISec Markets', icon: '🌍' },
    { path: '/aisec-scripts', label: 'AISec Scripts', icon: '📝' },
    { path: '/enquiry-checklist', label: 'Enquiry Response Checklist', icon: '📋' },
    { path: '/prospect-research', label: 'Prospect Research (Individuals)', icon: '🔍' },
    { path: '/company-prospect-research', label: 'Company List Building', icon: '🏢' },
    { path: '/job-role-analysis', label: 'Job Role Analysis', icon: '💼' }
  ]
  // SDR claim (sdr:true, not admin/editor): these nav items only; routes gated in App.jsx
  const sdrNavItems = [
    { path: '/deals', label: 'Deals', icon: '💼' },
    { path: '/files', label: 'Files', icon: '📁' },
    { path: '/notes', label: 'My Notes', icon: '📓' },
    { path: '/bant-ref', label: 'Bant', icon: '✅' },
    { path: '/icp-hub', label: 'ICP Hub', icon: '🎯' },
    { path: '/pricing-playbook', label: 'Pricing Playbook', icon: '💰' },
    { path: '/price-list', label: 'Price List', icon: '📋' },
    { path: '/aisec-markets', label: 'AISec Markets', icon: '🌍' },
    { path: '/aisec-scripts', label: 'AISec Scripts', icon: '📝' },
    { path: '/enquiry-checklist', label: 'Enquiry Response Checklist', icon: '📋' },
    { path: '/prospect-research', label: 'Prospect Research (individuals)', icon: '🔍' },
    { path: '/company-prospect-research', label: 'Company List Building', icon: '🏢' }
  ]
  const hasFullAccess = isAdmin || isEditor
  const visibleNavItems = hasFullAccess ? navItems : (isSdr ? sdrNavItems : navItems)

  const handleSignOut = async () => {
    try {
      await signOut()
      navigate('/login')
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }

  return (
    <div className="dashboard">
      <nav className="sidebar">
        <div className="sidebar-header">
          <h2>FileSig CRM</h2>
        </div>
        
        <nav className="sidebar-nav">
          {visibleNavItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`nav-item ${location.pathname.startsWith(item.path) ? 'active' : ''}`}
            >
              <span className="nav-icon">{item.icon}</span>
              <span className="nav-label">{item.label}</span>
            </Link>
          ))}
          {isEditor && (
            <Link
              to="/website-articles"
              className={`nav-item ${location.pathname === '/website-articles' ? 'active' : ''}`}
            >
              <span className="nav-icon">📝</span>
              <span className="nav-label">Filesig Website Articles</span>
            </Link>
          )}
          {isEditor && (
            <Link
              to="/devsecops-website-articles"
              className={`nav-item ${location.pathname === '/devsecops-website-articles' ? 'active' : ''}`}
            >
              <span className="nav-icon">🔒</span>
              <span className="nav-label">DevSecOps Website Articles</span>
            </Link>
          )}
          {(isEditor || isAdmin) && (
            <Link
              to="/linkedin-posts"
              className={`nav-item ${location.pathname === '/linkedin-posts' ? 'active' : ''}`}
            >
              <span className="nav-icon">💼</span>
              <span className="nav-label">LinkedIn Posts</span>
            </Link>
          )}
          {(isEditor || isAdmin) && (
            <Link
              to="/ad-image-creator"
              className={`nav-item ${location.pathname === '/ad-image-creator' ? 'active' : ''}`}
            >
              <span className="nav-icon">🖼️</span>
              <span className="nav-label">Ad Image Creator</span>
            </Link>
          )}
          {(isEditor || isAdmin) && (
            <Link
              to="/editors-note"
              className={`nav-item ${location.pathname === '/editors-note' ? 'active' : ''}`}
            >
              <span className="nav-icon">📰</span>
              <span className="nav-label">Editor&apos;s Note</span>
            </Link>
          )}
          {isAdmin && (
            <Link
              to="/investment"
              className={`nav-item ${location.pathname === '/investment' ? 'active' : ''}`}
            >
              <span className="nav-icon">📈</span>
              <span className="nav-label">Investment</span>
            </Link>
          )}
        </nav>
        
        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">
              {user?.photoURL ? (
                <img src={user.photoURL} alt={user.displayName} />
              ) : (
                <span>{user?.displayName?.[0] || user?.email?.[0] || 'U'}</span>
              )}
            </div>
            <div className="user-details">
              <div className="user-name-row">
                <p className="user-name">{user?.displayName || 'User'}</p>
                {isAdmin && (
                  <Link to="/admin" className="admin-link" title="Admin Settings">
                    ⚙️
                  </Link>
                )}
              </div>
              <p className="user-email">{user?.email}</p>
            </div>
          </div>
          <button className="signout-btn" onClick={handleSignOut}>
            Sign Out
          </button>
        </div>
      </nav>
      
      <main className="main-content">
        <div className="topbar">
          <button
            className="mobile-menu-btn"
            onClick={() => setShowMobileMenu(!showMobileMenu)}
          >
            ☰
          </button>
          <h1 className="page-title">
            {location.pathname === '/flows' ? 'Flows' :
             location.pathname === '/icp-hub' ? 'ICP Hub' :
             location.pathname === '/admin' ? 'Admin Settings' :
             location.pathname === '/investment' ? 'Investment' :
             location.pathname === '/website-articles' ? 'Filesig Website Articles' :
             location.pathname === '/devsecops-website-articles' ? 'DevSecOps Website Articles' :
             location.pathname === '/linkedin-posts' ? 'LinkedIn Posts' :
             location.pathname === '/ad-image-creator' ? 'Ad Image Creator' :
             location.pathname === '/editors-note' ? "Editor's Note" :
             location.pathname === '/notes' ? 'My Notes' :
             visibleNavItems.find(item => location.pathname === item.path || (item.path !== '/tags' && location.pathname.startsWith(item.path)))?.label || 'Dashboard'}
          </h1>
        </div>
        
        <div className="content-area">
          <Outlet />
        </div>
        
        <footer className="app-footer">
          <p>Private and Confidential — FileSig Company Use Only</p>
        </footer>
      </main>
      
      {showMobileMenu && (
        <div className="mobile-menu-overlay" onClick={() => setShowMobileMenu(false)}>
          <div className="mobile-menu" onClick={(e) => e.stopPropagation()}>
            {visibleNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`mobile-nav-item ${location.pathname.startsWith(item.path) ? 'active' : ''}`}
                onClick={() => setShowMobileMenu(false)}
              >
                <span className="nav-icon">{item.icon}</span>
                <span className="nav-label">{item.label}</span>
              </Link>
            ))}
            {isEditor && (
              <Link
                to="/website-articles"
                className={`mobile-nav-item ${location.pathname === '/website-articles' ? 'active' : ''}`}
                onClick={() => setShowMobileMenu(false)}
              >
                <span className="nav-icon">📝</span>
                <span className="nav-label">Filesig Website Articles</span>
              </Link>
            )}
            {isEditor && (
              <Link
                to="/devsecops-website-articles"
                className={`mobile-nav-item ${location.pathname === '/devsecops-website-articles' ? 'active' : ''}`}
                onClick={() => setShowMobileMenu(false)}
              >
                <span className="nav-icon">🔒</span>
                <span className="nav-label">DevSecOps Website Articles</span>
              </Link>
            )}
            {(isEditor || isAdmin) && (
              <Link
                to="/linkedin-posts"
                className={`mobile-nav-item ${location.pathname === '/linkedin-posts' ? 'active' : ''}`}
                onClick={() => setShowMobileMenu(false)}
              >
                <span className="nav-icon">💼</span>
                <span className="nav-label">LinkedIn Posts</span>
              </Link>
            )}
            {(isEditor || isAdmin) && (
              <Link
                to="/ad-image-creator"
                className={`mobile-nav-item ${location.pathname === '/ad-image-creator' ? 'active' : ''}`}
                onClick={() => setShowMobileMenu(false)}
              >
                <span className="nav-icon">🖼️</span>
                <span className="nav-label">Ad Image Creator</span>
              </Link>
            )}
            {isAdmin && (
              <Link
                to="/investment"
                className={`mobile-nav-item ${location.pathname === '/investment' ? 'active' : ''}`}
                onClick={() => setShowMobileMenu(false)}
              >
                <span className="nav-icon">📈</span>
                <span className="nav-label">Investment</span>
              </Link>
            )}
            {isAdmin && (
              <Link
                to="/admin"
                className={`mobile-nav-item ${location.pathname === '/admin' ? 'active' : ''}`}
                onClick={() => setShowMobileMenu(false)}
              >
                <span className="nav-icon">⚙️</span>
                <span className="nav-label">Admin</span>
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default Dashboard

